import React, { useEffect, useRef, useState } from 'react';
import '../styles/Partage.css';

function ShareModal({ link, title, intro, hint, onClose }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const inputRef = useRef(null);
  const resetTimer = useRef(null);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      clearTimeout(resetTimer.current);
    };
  }, [onClose]);

  const markCopied = () => {
    setCopied(true);
    setCopyError(false);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 2500);
  };

  const copyLink = async () => {
    if (window.umami) window.umami.track('share-copy-link');
    try {
      await navigator.clipboard.writeText(link);
      markCopied();
    } catch (err) {
      const input = inputRef.current;
      if (input) {
        input.focus();
        input.select();
        try {
          if (document.execCommand('copy')) {
            markCopied();
            return;
          }
        } catch (_) { /* ignore */ }
      }
      setCopyError(true);
    }
  };

  const nativeShare = async () => {
    if (window.umami) window.umami.track('share-native');
    try {
      await navigator.share({ title, text: title, url: link });
    } catch (_) { /* annulé par l'utilisateur */ }
  };

  const mailHref = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${title}\n\n${link}`)}`;

  return (
    <div className="ui-overlay kdo-share-overlay" onClick={onClose}>
      <div className="ui-modal kdo-share" role="dialog" aria-modal="true" aria-labelledby="kdo-share-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="kdo-share-close" onClick={onClose} aria-label="Fermer">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>

        <div className="kdo-share-head">
          <span className="kdo-share-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
          </span>
          <h2 id="kdo-share-title" className="kdo-share-title">{title}</h2>
        </div>

        {intro && <p className="kdo-share-intro">{intro}</p>}

        <ul className="kdo-share-points">
          <li>
            <span className="kdo-share-check" aria-hidden="true">✓</span>
            Ils peuvent consulter, sans rien pouvoir modifier.
          </li>
          <li>
            <span className="kdo-share-check" aria-hidden="true">✓</span>
            Ils peuvent marquer un article comme réservé.
          </li>
        </ul>

        <label className="kdo-share-label" htmlFor="kdo-share-link">Lien de partage</label>
        <div className="kdo-share-linkrow">
          <input
            id="kdo-share-link"
            ref={inputRef}
            className="kdo-share-link-input"
            type="text"
            readOnly
            value={link}
            onFocus={(e) => e.target.select()}
          />
        </div>

        <button type="button" className="kdo-share-primary" onClick={copyLink}>
          {copied ? '✓ Lien copié' : 'Copier le lien'}
        </button>
        <div className="kdo-share-status" role="status" aria-live="polite">
          {copyError ? "Copie impossible : sélectionnez le lien ci-dessus et copiez-le manuellement." : ''}
        </div>

        <div className="kdo-share-secondary">
          {canNativeShare && (
            <button type="button" className="kdo-share-secondary-btn" onClick={nativeShare}>Envoyer…</button>
          )}
          <a className="kdo-share-secondary-btn" href={mailHref} onClick={() => { if (window.umami) window.umami.track('share-mail'); }}>Par e-mail</a>
        </div>

        {hint && <p className="kdo-share-hint">{hint}</p>}
      </div>
    </div>
  );
}

export default ShareModal;
